import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Stop as StopIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';

const SftpManagement = () => {
  const [users, setUsers] = useState([]);
  const [fileLog, setFileLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    userName: '',
    vendorName: '',
    email: '',
    description: '',
    sshPublicKey: '',
    supportedFileTypes: 'settlement'
  });
  const [error, setError] = useState(null);
  const [errorsDialogOpen, setErrorsDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [stopProcessingDialogOpen, setStopProcessingDialogOpen] = useState(false);
  const [stopProcessingLoading, setStopProcessingLoading] = useState(false);
  const [forceStopAll, setForceStopAll] = useState(false);

  const { apiCall } = useApi();

  useEffect(() => {
    loadData();
  }, [tabValue]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (tabValue === 0) {
        await loadSftpUsers();
      } else {
        await loadFileProcessingLog();
      }
    } catch (error) {
      setError('Failed to load data');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSftpUsers = async () => {
    try {
      const data = await apiCall('/api/v1/sftp/users', 'GET');
      setUsers(data.users || []);
    } catch (error) {
      // Mock data for development
      setUsers([
        {
          userName: 'vendor-abc',
          vendorName: 'ABC Payment Solutions',
          email: 'admin@abcpay.com',
          homeDirectory: '/settlement-bucket/incoming/vendor-abc',
          supportedFileTypes: 'settlement',
          tags: [
            { Key: 'CreatedDate', Value: '2024-01-15' },
            { Key: 'CreatedBy', Value: 'admin@company.com' }
          ]
        },
        {
          userName: 'vendor-xyz',
          vendorName: 'XYZ Financial Services',
          email: 'files@xyzfinance.com',
          homeDirectory: '/settlement-bucket',
          supportedFileTypes: 'both',
          tags: [
            { Key: 'CreatedDate', Value: '2024-02-01' },
            { Key: 'CreatedBy', Value: 'admin@company.com' }
          ]
        }
      ]);
    }
  };

  const loadFileProcessingLog = async () => {
    try {
      const data = await apiCall('/api/v1/sftp/files', 'GET');
      setFileLog(data.files || []);
    } catch (error) {
      // Mock data for development
      setFileLog([
        {
          id: 1,
          fileName: 'settlements_2024_06_20.csv',
          vendorName: 'ABC Payment Solutions',
          status: 'SUCCESS',
          totalRecords: 1542,
          validRecords: 1540,
          insertedRecords: 1540,
          createdAt: '2024-06-20T08:30:00Z',
          processingTime: '45 seconds'
        },
        {
          id: 2,
          fileName: 'settlements_2024_06_19.csv',
          vendorName: 'XYZ Financial Services',
          status: 'FAILED',
          totalRecords: 2301,
          validRecords: 2250,
          insertedRecords: 0,
          errorMessage: 'Invalid date format in row 51',
          createdAt: '2024-06-19T14:15:00Z',
          processingTime: '12 seconds'
        }
      ]);
    }
  };

  const handleCreateUser = async () => {
    try {
      await apiCall('/api/v1/sftp/users', 'POST', formData);
      setDialogOpen(false);
      resetForm();
      loadSftpUsers();
    } catch (error) {
      setError('Failed to create SFTP user');
    }
  };

  const handleUpdateUser = async () => {
    try {
      await apiCall(`/api/v1/sftp/users/${selectedUser.userName}`, 'PUT', formData);
      setDialogOpen(false);
      resetForm();
      loadSftpUsers();
    } catch (error) {
      setError('Failed to update SFTP user');
    }
  };

  const handleDeleteUser = async (userName) => {
    if (window.confirm(`Are you sure you want to delete SFTP user '${userName}'?`)) {
      try {
        await apiCall(`/api/v1/sftp/users/${userName}`, 'DELETE');
        loadSftpUsers();
      } catch (error) {
        setError('Failed to delete SFTP user');
      }
    }
  };

  const openCreateDialog = () => {
    setSelectedUser(null);
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      userName: user.userName,
      vendorName: user.vendorName || getTagValue(user.tags, 'VendorName'),
      email: user.email || getTagValue(user.tags, 'Email'),
      description: user.description || getTagValue(user.tags, 'Description'),
      sshPublicKey: '',
      supportedFileTypes: user.supportedFileTypes || 'settlement'
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      userName: '',
      vendorName: '',
      email: '',
      description: '',
      sshPublicKey: '',
      supportedFileTypes: 'settlement'
    });
  };

  const getTagValue = (tags, key) => {
    const tag = tags?.find(t => t.Key === key);
    return tag ? tag.Value : '';
  };

  const handleViewErrors = async (file) => {
    try {
      if (!file.id) {
        setError('File ID is missing. Cannot retrieve validation errors.');
        return;
      }
      
      setSelectedFile(file);
      
      try {
        const errors = await apiCall(`/api/v1/sftp/files/${file.id}/errors`, 'GET');
        setValidationErrors(errors.errors || []);
      } catch (apiError) {
        console.warn('API call failed, using mock validation errors:', apiError);
        // Fallback to mock validation errors for development
        setValidationErrors([
          {
            rowNumber: 52,
            errorType: 'INVALID_DATE',
            errorMessage: 'Invalid settlement_date: xxxx',
            rowData: {
              terminal_id: '25001002',
              location_name: 'Dabbs Starkville',
              settlement_date: 'xxxx',
              amount: '955.00'
            }
          },
          {
            rowNumber: 153,
            errorType: 'MISSING_FIELDS',
            errorMessage: 'Missing required fields: amount',
            rowData: {
              terminal_id: '25001010',
              location_name: 'Sample Location',
              settlement_date: '2025-01-30'
            }
          }
        ]);
      }
      
      setErrorsDialogOpen(true);
    } catch (error) {
      console.error('Error loading validation errors:', error);
      setError(`Failed to load validation errors: ${error.message || 'Unknown error'}`);
    }
  };

  const handleRollback = (file) => {
    setSelectedFile(file);
    setRollbackDialogOpen(true);
  };

  const confirmRollback = async () => {
    try {
      setRollbackLoading(true);
      const result = await apiCall(`/api/v1/sftp/files/${selectedFile.id}/rollback`, 'POST');
      setRollbackDialogOpen(false);
      setSelectedFile(null);
      loadFileProcessingLog(); // Refresh the file log
      setError(null);
      // Show success message
      alert(`Successfully rolled back ${result.deletedRecords} records from ${result.fileName}`);
    } catch (error) {
      setError('Failed to rollback file data: ' + (error.message || 'Unknown error'));
    } finally {
      setRollbackLoading(false);
    }
  };

  const handleStopProcessing = (force = false) => {
    setForceStopAll(force);
    setStopProcessingDialogOpen(true);
  };

  const confirmStopProcessing = async () => {
    try {
      setStopProcessingLoading(true);
      const endpoint = forceStopAll ? '/api/v1/sftp/files/stop-processing?force=true' : '/api/v1/sftp/files/stop-processing';
      const result = await apiCall(endpoint, 'POST');
      setStopProcessingDialogOpen(false);
      loadFileProcessingLog(); // Refresh the file log
      setError(null);
      // Show success message
      alert(`Successfully stopped ${result.stoppedFiles.length} stuck file processing operations`);
    } catch (error) {
      setError('Failed to stop file processing: ' + (error.message || 'Unknown error'));
    } finally {
      setStopProcessingLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS': return 'success';
      case 'FAILED': return 'error';
      case 'PROCESSING': return 'warning';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Sample CSV data for each file type
  const sampleCSVData = {
    settlement: {
      filename: 'settlement_sample.csv',
      description: 'Settlement files contain daily transaction summaries by terminal with account matching',
      headers: 'TerminalID,LocationName,AccountHolder,AccountID,RTA,SettlementDate,ProcessedDate,DepositType,TransactionCount,Amount',
      sampleRows: [
        // Synthetic examples only — never paste real account/routing data here (it ships in the public JS bundle)
        '01001001,Sample Store,Sample Split Payee,10001,123456789012345678,2025-06-02,2025-06-02,SRCH,13,3.90',
        '01001001,Sample Store,Sample Store LLC,10002,987654321098765432,2025-06-02,2025-06-02,SETL,13,825.00',
        '01001012,Sample Store Two,Sample Holdings LLC,10003,111122223333444455,2025-06-02,2025-06-02,SETL,10,1350.00'
      ],
      requirements: [
        'TerminalID: 8-digit terminal identifier (required)',
        'LocationName: Location name (required)',
        'AccountHolder: Location account holder name (required)',
        'AccountID: Numeric account identifier for matching to transactions/accounts (required)',
        'RTA: Account routing identifier - up to 25-digit number (required)',
        'SettlementDate: Date in YYYY-MM-DD format (required)',
        'ProcessedDate: Processing date in YYYY-MM-DD format (required)',
        'DepositType: "SETL" for settlement, "SRCH" for surcharge (required)',
        'TransactionCount: Number of transactions (required, integer)',
        'Amount: Settlement amount in decimal format (required)'
      ],
      notes: [
        'Use commas as field separators',
        'Enclose fields containing commas in double quotes',
        'Amount should be in decimal format (e.g., 1234.56)',
        'Dates must be in YYYY-MM-DD format',
        'IMPORTANT: AccountID field is now required in all settlement files',
        'AccountID should be a 4-5 digit numeric identifier (e.g., 13584, 16104)',
        'Column order: TerminalID,LocationName,AccountHolder,AccountID,RTA,SettlementDate,ProcessedDate,DepositType,TransactionCount,Amount',
        'Each terminal may have multiple rows (SETL and SRCH with different AccountIDs)',
        'RTA field should be up to 25 digits (displayed as x#### in reports)'
      ]
    },
    transactions: {
      filename: 'transactions_sample.csv',
      description: 'Transaction files contain individual transaction details',
      headers: 'TerminalID,TransactionDateTime,TransactionType,Amount,Fee,ResponseCode,CardType,ProcessingTime',
      sampleRows: [
        '25001000,2025-01-30 08:15:23,WITHDRAWAL,40.00,2.50,00,DEBIT,1.2',
        '25001000,2025-01-30 09:22:45,WITHDRAWAL,60.00,2.50,00,DEBIT,0.8',
        '25001001,2025-01-30 10:33:12,BALANCE_INQUIRY,0.00,1.00,00,DEBIT,0.5'
      ],
      requirements: [
        'TerminalID: 8-digit terminal identifier (required)',
        'TransactionDateTime: Date and time in YYYY-MM-DD HH:mm:ss format (required)',
        'TransactionType: Type of transaction (WITHDRAWAL, BALANCE_INQUIRY, etc.) (required)',
        'Amount: Transaction amount in decimal format (required)',
        'Fee: Transaction fee in decimal format (required)',
        'ResponseCode: 2-digit response code (00 = success) (required)',
        'CardType: Type of card used (DEBIT, CREDIT, etc.) (required)',
        'ProcessingTime: Processing time in seconds (optional)'
      ],
      notes: [
        'DateTime format must include both date and time',
        'Response code 00 indicates successful transaction',
        'Amount and Fee should be in decimal format',
        'Common transaction types: WITHDRAWAL, BALANCE_INQUIRY, TRANSFER',
        'Processing time is optional but recommended for analytics'
      ]
    },
    accounts: {
      filename: 'accounts_sample.csv',
      description: 'Account files contain terminal and location account configurations',
      headers: 'Type,TerminalID,LocationName,AccountID,VaultAccount,SurchargeAccount,SplitPercentage,EffectiveDate',
      sampleRows: [
        'TERMINAL,25001000,ABC Store,xxxx0448,vault_001,surch_001,100.0,2025-01-01',
        'LOCATION,25001001,XYZ Shop,xxxx0449,vault_002,surch_002,85.0,2025-01-01',
        'LOCATION,,Main Street Group,xxxx0450,vault_003,surch_003,75.0,2025-01-01'
      ],
      requirements: [
        'Type: Record type (TERMINAL or LOCATION) (required)',
        'TerminalID: 8-digit terminal ID (required for TERMINAL type)',
        'LocationName: Location name (required for LOCATION type)',
        'AccountID: Account identifier in xxxx#### format (required)',
        'VaultAccount: Vault account identifier (required)',
        'SurchargeAccount: Surcharge account identifier (required)',
        'SplitPercentage: Percentage split as decimal (required)',
        'EffectiveDate: Date when configuration becomes active (required)'
      ],
      notes: [
        'Type field determines which other fields are required',
        'TERMINAL records require TerminalID, LocationName optional',
        'LOCATION records require LocationName, TerminalID should be empty',
        'Split percentage should be between 0.0 and 100.0',
        'Effective date determines when the configuration starts'
      ]
    }
  };

  const downloadSampleCSV = (fileType) => {
    const data = sampleCSVData[fileType];
    const csvContent = [
      data.headers,
      ...data.sampleRows
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', data.filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && children}
    </div>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          SFTP Management
        </Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadData}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          {tabValue === 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
            >
              Add Vendor
            </Button>
          )}
          {tabValue === 1 && (
            <Box>
              <Button
                variant="contained"
                color="error"
                startIcon={<StopIcon />}
                onClick={() => handleStopProcessing(false)}
                sx={{ mr: 1 }}
              >
                Stop All Processing
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<StopIcon />}
                onClick={() => handleStopProcessing(true)}
                size="small"
              >
                Force Stop All
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="SFTP Users" />
          <Tab label="File Processing Log" />
          <Tab label="Sample Files & Instructions" />
        </Tabs>
      </Paper>

      {/* SFTP Users Tab */}
      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Vendor Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>File Types</TableCell>
                  <TableCell>Home Directory</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.userName}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {user.userName}
                      </Typography>
                    </TableCell>
                    <TableCell>{user.vendorName || getTagValue(user.tags, 'VendorName')}</TableCell>
                    <TableCell>{user.email || getTagValue(user.tags, 'Email')}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.supportedFileTypes || 'settlement'} 
                        size="small" 
                        color={user.supportedFileTypes === 'both' ? 'success' : 
                               user.supportedFileTypes === 'transactions' ? 'info' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {user.homeDirectory}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(getTagValue(user.tags, 'CreatedDate'))}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(user)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteUser(user.userName)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* File Processing Log Tab */}
      <TabPanel value={tabValue} index={1}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Records</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Processing Time</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fileLog.map((file, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {file.fileName}
                      </Typography>
                      {file.errorMessage && (
                        <Typography variant="caption" color="error" display="block">
                          {file.errorMessage}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{file.vendorName}</TableCell>
                    <TableCell>
                      <Chip
                        label={file.status}
                        color={getStatusColor(file.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {file.insertedRecords || 0} / {file.totalRecords}
                      </Typography>
                      {file.validRecords !== file.totalRecords && (
                        <Typography variant="caption" color="warning.main" display="block">
                          {file.totalRecords - file.validRecords} invalid
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(file.createdAt)}</TableCell>
                    <TableCell>{file.processingTime}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        {(file.status === 'SUCCESS' && file.validRecords !== file.totalRecords) && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleViewErrors(file)}
                          >
                            View Errors
                          </Button>
                        )}
                        {(file.status === 'SUCCESS' && !file.isRolledBack) && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleRollback(file)}
                          >
                            Rollback
                          </Button>
                        )}
                        {file.isRolledBack && (
                          <Chip 
                            label="ROLLED BACK" 
                            size="small" 
                            color="error" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Sample Files & Instructions Tab */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          {Object.entries(sampleCSVData).map(([fileType, data]) => (
            <Grid item xs={12} key={fileType}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ textTransform: 'capitalize' }}>
                        {fileType} Files
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {data.description}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={() => downloadSampleCSV(fileType)}
                      size="small"
                    >
                      Download Sample
                    </Button>
                  </Box>
                  
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={3}>
                    {/* CSV Format Preview */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        CSV Format Preview
                      </Typography>
                      <Paper sx={{ 
                        p: 2, 
                        bgcolor: 'grey.50',
                        overflow: 'auto',
                        maxHeight: 200
                      }}>
                        <Typography variant="caption" sx={{ 
                          fontFamily: 'monospace', 
                          display: 'block', 
                          mb: 1,
                          wordBreak: 'break-all',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {data.headers}
                        </Typography>
                        {data.sampleRows.slice(0, 2).map((row, index) => (
                          <Typography key={index} variant="caption" sx={{ 
                            fontFamily: 'monospace', 
                            display: 'block',
                            wordBreak: 'break-all',
                            whiteSpace: 'pre-wrap'
                          }}>
                            {row}
                          </Typography>
                        ))}
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          ... and more rows
                        </Typography>
                      </Paper>
                    </Grid>
                    
                    {/* Field Requirements */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Field Requirements
                      </Typography>
                      <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                        {data.requirements.map((requirement, index) => (
                          <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                            • {requirement}
                          </Typography>
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  {/* Important Notes */}
                  <Typography variant="subtitle2" gutterBottom>
                    Important Notes
                  </Typography>
                  <Box>
                    {data.notes.map((note, index) => (
                      <Typography key={index} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        • {note}
                      </Typography>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {/* General Upload Instructions */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <DescriptionIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
              General Upload Instructions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  File Upload Process
                </Typography>
                <Typography variant="body2" paragraph>
                  1. Connect to the SFTP server using your assigned credentials
                </Typography>
                <Typography variant="body2" paragraph>
                  2. Navigate to the appropriate directory based on file type:
                </Typography>
                <Typography variant="body2" sx={{ ml: 2, mb: 1 }}>
                  • Settlement files: Upload to your main directory
                </Typography>
                <Typography variant="body2" sx={{ ml: 2, mb: 2 }}>
                  • Transaction files: Upload to transactions/incoming/
                </Typography>
                <Typography variant="body2" paragraph>
                  3. Upload your CSV file (files are processed automatically)
                </Typography>
                <Typography variant="body2">
                  4. Monitor processing status in the "File Processing Log" tab
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  File Naming Recommendations
                </Typography>
                <Typography variant="body2" paragraph>
                  Use descriptive filenames that include:
                </Typography>
                <Typography variant="body2" sx={{ ml: 2, mb: 1 }}>
                  • File type (settlement, transactions, accounts)
                </Typography>
                <Typography variant="body2" sx={{ ml: 2, mb: 1 }}>
                  • Date (YYYY-MM-DD format)
                </Typography>
                <Typography variant="body2" sx={{ ml: 2, mb: 2 }}>
                  • Optional sequence number if multiple files per day
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                  Examples:<br />
                  settlements_2025-01-30.csv<br />
                  transactions_2025-01-30_001.csv<br />
                  accounts_2025-01-30.csv
                </Typography>
              </Grid>
            </Grid>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Processing Time:</strong> Files are typically processed within 1-2 minutes of upload. 
                Large files may take longer. Check the "File Processing Log" tab for real-time status updates.
              </Typography>
            </Alert>
          </CardContent>
        </Card>

        {/* SSH Key Setup Instructions */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🔐 SSH Key Setup Instructions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Important:</strong> SFTP access requires SSH key authentication. You must generate an SSH key pair and send us the public key before accessing the server.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              {/* Windows Users */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  For Windows Users (PowerShell)
                </Typography>
                <Typography variant="body2" paragraph>
                  1. Open PowerShell (Search → "PowerShell" → Click blue icon)
                </Typography>
                <Typography variant="body2" paragraph>
                  2. Run this command:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'white', mb: 2 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    ssh-keygen -t rsa -b 2048 -f settlement-key
                  </Typography>
                </Paper>
                <Typography variant="body2" paragraph>
                  3. Press Enter twice (no passphrase)
                </Typography>
                <Typography variant="body2" paragraph>
                  4. View your public key:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'white', mb: 2 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    notepad settlement-key.pub
                  </Typography>
                </Paper>
                <Typography variant="body2">
                  5. Copy ALL the text and email it to us
                </Typography>
              </Grid>

              {/* Mac Users */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  For Mac Users (Terminal)
                </Typography>
                <Typography variant="body2" paragraph>
                  1. Open Terminal (Cmd+Space → "Terminal" → Enter)
                </Typography>
                <Typography variant="body2" paragraph>
                  2. Run this command:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'white', mb: 2 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    ssh-keygen -t rsa -b 2048 -f settlement-key
                  </Typography>
                </Paper>
                <Typography variant="body2" paragraph>
                  3. Press Enter twice (no passphrase)
                </Typography>
                <Typography variant="body2" paragraph>
                  4. Display your public key:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'white', mb: 2 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    cat settlement-key.pub
                  </Typography>
                </Paper>
                <Typography variant="body2">
                  5. Copy the text that appears and email it to us
                </Typography>
              </Grid>

              {/* Windows SYSTEM Account */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  For Windows SYSTEM Account (Automated Services)
                </Typography>
                <Typography variant="body2" paragraph>
                  If you need to set up SSH keys for a Windows service or automated process:
                </Typography>
                <Typography variant="body2" paragraph>
                  1. Download PsExec from Microsoft Sysinternals
                </Typography>
                <Typography variant="body2" paragraph>
                  2. Run Command Prompt as Administrator, then:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'white', mb: 2 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    psexec -i -s cmd.exe<br />
                    cd C:\Windows\System32\config\systemprofile<br />
                    mkdir .ssh<br />
                    cd .ssh<br />
                    ssh-keygen -t rsa -b 2048 -f settlement-key -N ""<br />
                    type settlement-key.pub
                  </Typography>
                </Paper>
                <Typography variant="body2">
                  3. Copy the output and email it to us
                </Typography>
              </Grid>

              {/* PuTTY Alternative */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Alternative: Using PuTTY (Windows)
                </Typography>
                <Typography variant="body2" paragraph>
                  1. Download and install PuTTY
                </Typography>
                <Typography variant="body2" paragraph>
                  2. Open PuTTYgen
                </Typography>
                <Typography variant="body2" paragraph>
                  3. Click "Generate" and move mouse randomly
                </Typography>
                <Typography variant="body2" paragraph>
                  4. Copy the public key text from the box
                </Typography>
                <Typography variant="body2">
                  5. Save private key and email us the public key
                </Typography>
              </Grid>

              {/* What to Send */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  What to Send Us
                </Typography>
                <Typography variant="body2" paragraph>
                  Email the public key with:
                </Typography>
                <Typography variant="body2" sx={{ ml: 2, mb: 1 }}>
                  • Subject: "SSH Key for SFTP - [Company Name]"
                </Typography>
                <Typography variant="body2" sx={{ ml: 2, mb: 1 }}>
                  • The public key text (starts with "ssh-rsa")
                </Typography>
                <Typography variant="body2" sx={{ ml: 2, mb: 1 }}>
                  • Your company name and contact email
                </Typography>
                <Typography variant="body2" sx={{ ml: 2, mb: 2 }}>
                  • Requested username for SFTP access
                </Typography>
                
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Security:</strong> Only send the PUBLIC key (ends with .pub). Never send the private key file!
                  </Typography>
                </Alert>
              </Grid>
            </Grid>

            {/* Server Details */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              SFTP Server Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" paragraph>
                  <strong>Server:</strong> s-413a578272f746299.server.transfer.us-east-1.amazonaws.com
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Port:</strong> 22 (default SFTP)
                </Typography>
                <Typography variant="body2">
                  <strong>Authentication:</strong> SSH Key Only (no passwords)
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Recommended SFTP Clients:
                </Typography>
                <Typography variant="body2" sx={{ ml: 2, mb: 0.5 }}>
                  • Windows: WinSCP, FileZilla
                </Typography>
                <Typography variant="body2" sx={{ ml: 2, mb: 0.5 }}>
                  • Mac: Cyberduck, FileZilla
                </Typography>
                <Typography variant="body2" sx={{ ml: 2 }}>
                  • Command line: sftp, rsync
                </Typography>
              </Grid>
            </Grid>

            {/* Troubleshooting */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Common Issues
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" paragraph>
                  <strong>"ssh-keygen not found":</strong> Install OpenSSH for Windows or use PuTTY method
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>"Permission denied":</strong> Run PowerShell as Administrator
                </Typography>
                <Typography variant="body2">
                  <strong>"Can't find files":</strong> Check your home directory (C:\Users\[YourName]\ on Windows)
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" paragraph>
                  <strong>"Key looks wrong":</strong> Make sure it starts with "ssh-rsa" or "ssh-ed25519"
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>"Connection refused":</strong> Verify server address and that your account was created
                </Typography>
                <Typography variant="body2">
                  <strong>"Access denied":</strong> Check that you're using the correct private key file
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Create/Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedUser ? 'Edit SFTP User' : 'Create SFTP User'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                value={formData.userName}
                onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                disabled={!!selectedUser}
                helperText="Lowercase letters, numbers, and hyphens only"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vendor Name"
                value={formData.vendorName}
                onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Supported File Types</InputLabel>
                <Select
                  value={formData.supportedFileTypes}
                  onChange={(e) => setFormData({ ...formData, supportedFileTypes: e.target.value })}
                  label="Supported File Types"
                >
                  <MenuItem value="settlement">Settlement Files Only</MenuItem>
                  <MenuItem value="transactions">Transaction Files Only</MenuItem>
                  <MenuItem value="both">Both Settlement and Transaction Files</MenuItem>
                </Select>
                <FormHelperText>
                  Determines which types of files this vendor can upload via SFTP
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="SSH Public Key"
                multiline
                rows={4}
                value={formData.sshPublicKey}
                onChange={(e) => setFormData({ ...formData, sshPublicKey: e.target.value })}
                placeholder="ssh-rsa AAAAB3NzaC1yc2EAAAADAQABAAAB..."
                helperText={selectedUser ? "Leave empty to keep existing key" : "Required for new users"}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={selectedUser ? handleUpdateUser : handleCreateUser}
            disabled={!formData.userName || !formData.vendorName || (!selectedUser && !formData.sshPublicKey)}
          >
            {selectedUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Connection Information Card */}
      {tabValue === 0 && users.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              SFTP Connection Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" paragraph>
              <strong>Server:</strong> your-sftp-server.server.transfer.region.amazonaws.com
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Port:</strong> 22
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Protocol:</strong> SFTP
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Authentication:</strong> SSH Key pairs only
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload CSV files to your assigned directory based on file type:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              • Settlement files: Upload to your main directory (incoming/username/)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Transaction files: Upload to transactions/incoming/username/
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Files will be automatically processed upon upload.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Validation Errors Dialog */}
      <Dialog open={errorsDialogOpen} onClose={() => setErrorsDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Validation Errors - {selectedFile?.fileName}
        </DialogTitle>
        <DialogContent>
          {validationErrors.length === 0 ? (
            <Typography>No validation errors found.</Typography>
          ) : (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Error Type</TableCell>
                    <TableCell>Error Message</TableCell>
                    <TableCell>Row Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validationErrors.map((error, index) => (
                    <TableRow key={index}>
                      <TableCell>{error.rowNumber}</TableCell>
                      <TableCell>
                        <Chip label={error.errorType} size="small" color="error" />
                      </TableCell>
                      <TableCell>{error.errorMessage}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {JSON.stringify(error.rowData, null, 2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Rollback Confirmation Dialog */}
      <Dialog open={rollbackDialogOpen} onClose={() => setRollbackDialogOpen(false)}>
        <DialogTitle>Confirm Rollback</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to rollback the data from this file?
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
            File: {selectedFile?.fileName}
          </Typography>
          <Typography variant="body2">
            Records inserted: {selectedFile?.insertedRecords}
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This will permanently delete all settlement records that were inserted from this file. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackDialogOpen(false)} disabled={rollbackLoading}>
            Cancel
          </Button>
          <Button 
            onClick={confirmRollback} 
            color="error" 
            variant="contained"
            disabled={rollbackLoading}
          >
            {rollbackLoading ? 'Rolling back...' : 'Confirm Rollback'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stop Processing Confirmation Dialog */}
      <Dialog open={stopProcessingDialogOpen} onClose={() => setStopProcessingDialogOpen(false)}>
        <DialogTitle>{forceStopAll ? 'Force Stop All File Processing' : 'Stop All File Processing'}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to stop all stuck file processing operations?
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            This will mark all files that have been processing for more than {forceStopAll ? '30 seconds' : '5 minutes'} as FAILED.
          </Typography>
          {forceStopAll && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              <strong>Force mode:</strong> This will stop ALL processing files, including recently started ones.
            </Typography>
          )}
          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
            Files that are currently being processed successfully will be stopped and marked as failed.
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone, but failed files can be reprocessed manually.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopProcessingDialogOpen(false)} disabled={stopProcessingLoading}>
            Cancel
          </Button>
          <Button 
            onClick={confirmStopProcessing} 
            color="error" 
            variant="contained"
            disabled={stopProcessingLoading}
          >
            {stopProcessingLoading ? 'Stopping...' : (forceStopAll ? 'Force Stop All' : 'Stop All Processing')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SftpManagement;